import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { CreditCard, CheckCircle2, AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import TenantFiscalForm from "./TenantFiscalForm";
import TenantReceipts from "./TenantReceipts";

const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("it-IT") : "—");

function StatusPill({ b }) {
  if (!b.paid_until) return <span className="pill bg-zinc-500/15 text-zinc-500" data-testid="billing-status">senza scadenza</span>;
  if (!b.active) return <span className="pill bg-red-500/15 text-red-500" data-testid="billing-status">club sospeso</span>;
  const map = { expired: ["bg-red-500/15 text-red-500", `scaduto da ${Math.abs(b.days_left)} gg`], expiring: ["bg-amber-500/15 text-amber-600", `scade tra ${b.days_left} gg`], ok: ["bg-emerald-500/15 text-emerald-600", "attivo"] };
  const [cls, label] = map[b.status] || map.ok;
  return <span className={`pill ${cls}`} data-testid="billing-status">{label}</span>;
}

export const BillingCard = () => {
  const [b, setB] = useState(null);
  const [months, setMonths] = useState(1);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(null); // {session_id, tries}
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/billing/me").then(({ data }) => setB(data)).catch(() => setB(false));
  const [paypal, setPaypal] = useState(null);
  useEffect(() => { load(); api.get("/billing/paypal/status").then(({ data }) => setPaypal(data)).catch(() => setPaypal(null)); }, []);
  // Ritorno da PayPal: ?paypal=success&token=<orderId> → capture lato server
  useEffect(() => {
    const st = params.get("paypal"), token = params.get("token");
    if (!st) return;
    const next = new URLSearchParams(params); ["paypal", "token", "PayerID"].forEach((k) => next.delete(k)); setParams(next, { replace: true });
    if (st === "success" && token) {
      setBusy(true);
      api.post("/billing/paypal/capture", { paypal_order_id: token })
        .then(() => { toast.success("Pagamento PayPal completato: canone rinnovato"); load(); window.dispatchEvent(new Event("billing-updated")); })
        .catch((e) => toast.error(formatApiError(e))).finally(() => setBusy(false));
    } else if (st === "cancel") toast.info("Pagamento PayPal annullato");
    // eslint-disable-next-line
  }, []);
  const payPayPal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/paypal/checkout", { months, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  useEffect(() => {
    const flag = params.get("billing"); const sid = params.get("session_id");
    if (flag === "cancel") { toast.info("Pagamento annullato"); clearParams(); }
    else if (flag === "success" && sid) setPolling({ session_id: sid, tries: 0, kind: "pay" });
    else if (flag === "autorenew" && sid) setPolling({ session_id: sid, tries: 0, kind: "autorenew" });
    else if (flag === "card") setPolling({ tries: 0, kind: "card" });
    // eslint-disable-next-line
  }, []);

  const clearParams = () => { const p = new URLSearchParams(params); p.delete("billing"); p.delete("session_id"); setParams(p, { replace: true }); };

  useEffect(() => {
    if (!polling) return;
    if (polling.tries > 10) { toast.error("Operazione in verifica: controlla tra qualche minuto"); setPolling(null); clearParams(); return; }
    const t = setTimeout(async () => {
      try {
        if (polling.kind === "card") {
          const { data } = await api.post("/billing/auto-renew/sync");
          setB(data); toast.success(`Carta aggiornata${data.auto_renew?.card ? `: ${data.auto_renew.card}` : ""}`); setPolling(null); clearParams(); return;
        } else if (polling.kind === "autorenew") {
          const { data } = await api.get(`/billing/auto-renew/status/${polling.session_id}`);
          if (data.auto_renew?.enabled) { toast.success(`Addebito automatico attivato${data.auto_renew.next_charge ? ` · prossimo addebito ${fmtDate(data.auto_renew.next_charge)}` : ""}`); setPolling(null); clearParams(); load(); window.dispatchEvent(new Event("billing-updated")); return; }
          if (data.payment_status === "expired") { toast.error("Attivazione non completata"); setPolling(null); clearParams(); return; }
        } else {
          const { data } = await api.get(`/billing/status/${polling.session_id}`);
          if (data.payment_status === "paid") { toast.success(`Canone rinnovato fino al ${fmtDate(data.paid_until)}`); setPolling(null); clearParams(); load(); window.dispatchEvent(new Event("billing-updated")); return; }
          if (["expired", "failed"].includes(data.payment_status)) { toast.error("Pagamento non riuscito"); setPolling(null); clearParams(); return; }
        }
      } catch { /* retry */ }
      setPolling({ ...polling, tries: polling.tries + 1 });
    }, polling.tries === 0 ? 300 : 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [polling]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { months, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  const enableAuto = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/auto-renew/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  const changeCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/auto-renew/card", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  const disableAuto = async () => {
    if (!window.confirm("Disattivare l'addebito automatico? Il periodo già pagato resta valido; alla scadenza dovrai rinnovare manualmente.")) return;
    setBusy(true);
    try { const { data } = await api.post("/billing/auto-renew/cancel"); setB(data); toast.success("Addebito automatico disattivato"); window.dispatchEvent(new Event("billing-updated")); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (b === null || b === false) return null;
  const opt = b.options.find((o) => o.months === months) || b.options[0];
  const ar = b.auto_renew || {};
  return (
    <div className="card-elev p-5 space-y-4" data-testid="billing-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-heading font-semibold text-lg flex items-center gap-2"><CreditCard className="h-5 w-5" /> Canone del club</h3>
        <StatusPill b={b} />
      </div>
      {polling && <div className="flex items-center gap-2 text-sm text-sky-700" data-testid="billing-verifying"><Loader2 className="h-4 w-4 animate-spin" /> {polling.kind === "autorenew" ? "Attivazione dell'addebito automatico in corso…" : polling.kind === "card" ? "Aggiornamento della carta in corso…" : "Verifica del pagamento in corso…"}</div>}
      {!b.active && <div className="flex items-start gap-2 rounded-lg bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-700" data-testid="billing-suspended"><AlertTriangle className="h-4 w-4 mt-0.5" /> Il club è sospeso{b.suspended_reason ? ` (${b.suspended_reason})` : ""}: rinnova il canone per riattivarlo subito.</div>}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><div className="order-field-label">Canone mensile</div><div className="font-mono font-bold" data-testid="billing-fee">{b.monthly_fee > 0 ? `${eur(b.monthly_fee)}/mese` : "gratuito"}</div></div>
        <div><div className="order-field-label">Pagato fino al</div><div className="font-mono font-bold" data-testid="billing-paid-until">{fmtDate(b.paid_until)}</div></div>
      </div>
      {b.online_payment && (
        <div className="space-y-3 border-t border-zinc-800 pt-4" data-testid="billing-auto-renew">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2"><RefreshCw className={`h-4 w-4 ${ar.enabled ? "text-emerald-500" : "text-zinc-500"}`} /><span className="text-sm font-semibold">Addebito automatico mensile</span></div>
            {ar.enabled
              ? <span className="pill bg-emerald-500/15 text-emerald-600" data-testid="billing-auto-renew-status">{ar.status === "past_due" ? "pagamento in sospeso" : ar.status === "trialing" ? "attivo · parte alla scadenza" : "attivo"}</span>
              : <span className="pill bg-zinc-500/15 text-zinc-500" data-testid="billing-auto-renew-status">non attivo</span>}
          </div>
          {ar.enabled ? (
            <>
              <p className="text-xs text-zinc-500" data-testid="billing-auto-renew-info">
                {ar.card ? `Carta ${ar.card}` : "Carta salvata"}{ar.next_charge ? ` · prossimo addebito ${eur(b.monthly_fee)} il ${fmtDate(ar.next_charge)}` : ""}. Il canone non scade mai: ogni mese viene addebitato automaticamente.
              </p>
              {ar.last_error && <p className="text-xs text-red-600" data-testid="billing-auto-renew-error">{ar.last_error}</p>}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={changeCard} disabled={busy || !!polling} data-testid="billing-auto-renew-card" className="border-zinc-800">{busy ? "Reindirizzo a Stripe…" : "Aggiorna carta"}</Button>
                <Button variant="outline" size="sm" onClick={disableAuto} disabled={busy} data-testid="billing-auto-renew-off" className="border-zinc-800">Disattiva addebito automatico</Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-zinc-500">Salva una carta e il canone di {eur(b.monthly_fee)} viene addebitato ogni mese senza pensieri. {b.paid_until && b.days_left > 2 ? `Il primo addebito avverrà alla scadenza attuale (${fmtDate(b.paid_until)}).` : "Il primo addebito avviene subito."} Puoi disattivarlo quando vuoi.</p>
              <Button variant="outline" size="sm" onClick={enableAuto} disabled={busy || !!polling} data-testid="billing-auto-renew-on" className="border-emerald-600/40 text-emerald-700 hover:bg-emerald-500/10">
                {busy ? "Reindirizzo a Stripe…" : "Attiva addebito automatico"}
              </Button>
            </>
          )}
        </div>
      )}
      {b.online_payment && !ar.enabled && (
        <div className="space-y-3 border-t border-zinc-800 pt-4">
          <div className="order-field-label">Rinnova online con carta</div>
          <div className="flex flex-wrap gap-2">
            {b.options.map((o) => (
              <button key={o.months} type="button" onClick={() => setMonths(o.months)} data-testid={`billing-months-${o.months}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${months === o.months ? "bg-[color:var(--vivi)] text-white border-transparent" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}>
                {o.months} mes{o.months === 1 ? "e" : "i"} · {eur(o.amount)}
              </button>
            ))}
          </div>
          <Button onClick={pay} disabled={busy || !!polling} data-testid="billing-pay" className="bg-[color:var(--vivi)] text-white">
            {busy ? "Reindirizzo…" : `Rinnova ora · ${eur(opt.amount)}`}
          </Button>
          {paypal?.enabled && (
            <Button onClick={payPayPal} disabled={busy || !!polling} variant="outline" data-testid="billing-pay-paypal" className="ml-2 border-sky-400 text-sky-700 hover:bg-sky-50">
              {busy ? "Reindirizzo…" : `Paga con PayPal · ${eur(opt.amount)}`}
            </Button>
          )}
          <p className="text-[11px] text-zinc-500">Pagamento sicuro con Stripe{paypal?.enabled ? " o PayPal" : ""}. La nuova scadenza parte dalla data attuale di scadenza (o da oggi se già scaduto).</p>
        </div>
      )}
      {b.payments?.length > 0 && (
        <div className="border-t border-zinc-800 pt-3" data-testid="billing-history">
          <div className="order-field-label mb-1">Ultimi pagamenti</div>
          <ul className="text-xs space-y-1">
            {b.payments.map((p) => (
              <li key={p.id} className="flex justify-between gap-2" data-testid={`billing-payment-${p.id}`}>
                <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-500" /> {fmtDate(p.at)} · {p.months} mes{p.months === 1 ? "e" : "i"} {p.method === "stripe" ? "· carta" : ""}</span>
                <span className="font-mono">{eur(p.amount)} → {fmtDate(p.to)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {b.online_payment && <TenantReceipts refreshKey={b.paid_until + (b.payments?.length || 0)} />}
      {b.online_payment && <TenantFiscalForm />}
    </div>
  );
};

export default BillingCard;
